/**
 * "Not found" error patterns
 * Extracted from constants.ts to reduce file size
 */

import type { UserFriendlyError } from "../types";
import { organizationDemonstrativeTarget, organizationNotFoundMessage, organizationNotFoundTitle } from "@Front/Utils/publicSiteBranding";

/**
 * Specific "not found" patterns with details extraction
 */
export const notFoundErrorPatterns: Array<{
	pattern: RegExp;
	message: UserFriendlyError | ((match: RegExpMatchArray) => UserFriendlyError);
}> = [
	{
		pattern: /folder not found[:\s]+([a-f0-9-]+)/i,
		message: (match: RegExpMatchArray): UserFriendlyError => {
			const folderUid = match[1]?.substring(0, 8);
			return {
				title: "Dossier introuvable",
				message: folderUid
					? `Le dossier demandé (${folderUid}...) n'existe pas, a été supprimé ou vous n'y avez pas accès.`
					: "Le dossier demandé n'existe pas, a été supprimé ou vous n'y avez pas accès.",
				canRetry: false,
			};
		},
	},
	{
		pattern: /office folder not found/i,
		message: {
			title: "Dossier introuvable",
			message: "Le dossier demandé n'existe pas, a été supprimé ou vous n'y avez pas accès.",
			canRetry: false,
		},
	},
	{
		pattern: /document not found[:\s]+([a-f0-9-]+)/i,
		message: (match: RegExpMatchArray): UserFriendlyError => {
			const documentUid = match[1]?.substring(0, 8);
			return {
				title: "Document introuvable",
				message: documentUid
					? `Le document demandé (${documentUid}...) n'existe pas, a été supprimé ou vous n'y avez pas accès.`
					: "Le document demandé n'existe pas, a été supprimé ou vous n'y avez pas accès.",
				canRetry: false,
			};
		},
	},
	{
		pattern: /^document not found$/i,
		message: {
			title: "Document introuvable",
			message: "Le document demandé n'existe pas, a été supprimé ou vous n'y avez pas accès.",
			canRetry: false,
		},
	},
	{
		pattern: /file not found/i,
		message: {
			title: "Fichier introuvable",
			message: "Le fichier demandé n'existe pas, a été supprimé ou vous n'y avez pas accès.",
			canRetry: false,
		},
	},
	{
		pattern: /share not found/i,
		message: {
			title: "Partage introuvable",
			message: "Le partage de dossier demandé n'existe pas ou a été révoqué.",
			canRetry: false,
		},
	},
	{
		pattern: /user not found/i,
		message: {
			title: "Utilisateur introuvable",
			message: "L'utilisateur demandé n'existe pas ou n'est plus actif.",
			canRetry: false,
		},
	},
	{
		pattern: /office not found/i,
		message: {
			title: organizationNotFoundTitle(),
			message: organizationNotFoundMessage(),
			canRetry: false,
		},
	},
	{
		pattern: /third party not found/i,
		message: {
			title: "Tiers introuvable",
			message: "Le tiers demandé n'existe pas ou n'est plus associé à ce dossier.",
			canRetry: false,
		},
	},
	{
		pattern: /plan\s+([a-f0-9-]+)\s+not found|subscription plan.*not found/i,
		message: {
			title: "Plan d'abonnement introuvable",
			message: "Le plan d'abonnement demandé n'existe pas ou n'est plus disponible.",
			canRetry: false,
		},
	},
	{
		pattern: /document type not found/i,
		message: {
			title: "Type de document introuvable",
			message: "Le type de document demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
	{
		pattern: /deed not found/i,
		message: {
			title: "Acte introuvable",
			message: "L'acte demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
	{
		pattern: /subscription not found/i,
		message: {
			title: "Abonnement introuvable",
			message: "L'abonnement demandé n'existe pas ou n'est plus actif.",
			canRetry: false,
		},
	},
	{
		pattern: /no anchor found for (?:folder|document)[:\s]+([a-f0-9-]+)/i,
		message: (match: RegExpMatchArray): UserFriendlyError => {
			const uid = match[1]?.substring(0, 8);
			return {
				title: "Ancrage introuvable",
				message: uid
					? `Aucun ancrage blockchain trouvé pour cet élément (${uid}...). Il n'a peut-être pas encore été ancré.`
					: "Aucun ancrage blockchain trouvé pour cet élément. Il n'a peut-être pas encore été ancré.",
				canRetry: false,
			};
		},
	},
	{
		pattern: /no anchor found for hash[:\s]+([a-f0-9]+)/i,
		message: (match: RegExpMatchArray): UserFriendlyError => {
			const hash = match[1]?.substring(0, 16);
			return {
				title: "Ancrage introuvable",
				message: hash ? `Aucun ancrage blockchain trouvé pour le hash ${hash}...` : "Aucun ancrage blockchain trouvé pour ce hash.",
				canRetry: false,
			};
		},
	},
	{
		pattern: /no reminder config found for document type[:\s]+([a-f0-9-]+)/i,
		message: {
			title: "Configuration de rappel introuvable",
			message: "Aucune configuration de rappel n'a été définie pour ce type de document.",
			canRetry: false,
		},
	},
	{
		pattern: /site text not found for uid[:\s]+"([^"]+)"/i,
		message: (match: RegExpMatchArray): UserFriendlyError => {
			const uid = match[1];
			return {
				title: "Texte introuvable",
				message: `Le texte demandé (${uid}) n'existe pas ou n'est pas configuré.`,
				canRetry: false,
			};
		},
	},
	{
		pattern: /appointment not found/i,
		message: {
			title: "Rendez-vous introuvable",
			message: "Le rendez-vous demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
	{
		pattern: /vote not found/i,
		message: {
			title: "Vote introuvable",
			message: "Le vote demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
	{
		pattern: /user notification not found/i,
		message: {
			title: "Notification introuvable",
			message: "La notification demandée n'existe pas ou a été supprimée.",
			canRetry: false,
		},
	},
	{
		pattern: /officeRole not found/i,
		message: {
			title: "Rôle introuvable",
			message: "Le rôle demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
	{
		pattern: /no rib file configured for this office/i,
		message: {
			title: "RIB non configuré",
			message: `Aucun fichier RIB n'a été configuré pour ${organizationDemonstrativeTarget()}.`,
			canRetry: false,
		},
	},
	{
		pattern: /record.*not found|not found/i,
		message: {
			title: "Élément introuvable",
			message: "L'élément demandé n'existe pas ou a été supprimé.",
			canRetry: false,
		},
	},
];

